import {
	useEffect,
	useRef,
	useState,
	type PointerEvent,
	type ReactNode,
} from 'react'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'

/** Pixels a mouse moves before a press counts as a drag, not a click. */
const dragThreshold = 4

/**
 * The inline offset of `container` content: its centred box plus its gutter.
 * `100%` is the width of the rail's parent, so the rail must sit full-bleed,
 * where it is the page width without the scrollbar that `container` centres
 * in.
 */
const containerInset =
	'calc(max((100% - var(--container-max)) / 2, 0px) + var(--container-gutter))'

/**
 * Rail: a horizontal row of cards that bleeds to the viewport edge. The first
 * card lines up with `container` content, the rest run off to the right.
 * Touch swipes it natively, a mouse drags it (the click ending a drag does
 * not reach the card underneath), and cards snap to the start. While the
 * cards overflow, previous and next buttons step one card and a line shows
 * how far the rail is scrolled.
 */
export function Rail({
	heading,
	items,
	previousLabel,
	nextLabel,
	className,
}: {
	heading: ReactNode
	items: { key: string; node: ReactNode }[]
	previousLabel: string
	nextLabel: string
	className?: string
}) {
	const scrollerRef = useRef<HTMLDivElement>(null)
	const drag = useRef<{ x: number; left: number; moved: boolean } | null>(null)
	const [overflows, setOverflows] = useState(false)
	const [progress, setProgress] = useState(0)

	function measure() {
		const scroller = scrollerRef.current
		if (!scroller) return
		const max = scroller.scrollWidth - scroller.clientWidth
		const left = scroller.scrollLeft
		setOverflows(max > 1)
		// Zoom and snapping leave scrollLeft a fraction of a pixel off either
		// end, which still counts as that end.
		setProgress(left < 1 ? 0 : left > max - 1 ? 1 : left / max)
	}

	useEffect(() => {
		const scroller = scrollerRef.current
		if (!scroller) return
		measure()
		const observer = new ResizeObserver(measure)
		observer.observe(scroller)
		return () => observer.disconnect()
	}, [items.length])

	function step(direction: 1 | -1) {
		const scroller = scrollerRef.current
		const card = scroller?.querySelector<HTMLElement>('[data-slot="rail-item"]')
		if (!scroller || !card) return
		const gap = parseFloat(getComputedStyle(scroller).columnGap) || 0
		const reduced = window.matchMedia(
			'(prefers-reduced-motion: reduce)',
		).matches
		scroller.scrollBy({
			left: direction * (card.offsetWidth + gap),
			behavior: reduced ? 'auto' : 'smooth',
		})
	}

	function startDrag(event: PointerEvent<HTMLDivElement>) {
		if (event.pointerType !== 'mouse' || event.button !== 0) return
		drag.current = {
			x: event.clientX,
			left: event.currentTarget.scrollLeft,
			moved: false,
		}
	}

	function moveDrag(event: PointerEvent<HTMLDivElement>) {
		if (!drag.current) return
		const dx = event.clientX - drag.current.x
		if (!drag.current.moved) {
			if (Math.abs(dx) <= dragThreshold) return
			drag.current.moved = true
			// Snapping would pull the rail back under the pointer. The capture
			// keeps the drag going once the pointer leaves the rail; it starts
			// only past the threshold, so a plain click still reaches its link.
			event.currentTarget.style.scrollSnapType = 'none'
			event.currentTarget.setPointerCapture?.(event.pointerId)
		}
		event.currentTarget.scrollLeft = drag.current.left - dx
	}

	function endDrag(event: PointerEvent<HTMLDivElement>) {
		if (!drag.current) return
		const { moved } = drag.current
		drag.current = null
		if (!moved) return
		event.currentTarget.style.scrollSnapType = ''
		// The click that ends a drag fires right after pointerup; it must not
		// follow the card's link.
		const swallow = (click: Event) => {
			click.preventDefault()
			click.stopPropagation()
		}
		window.addEventListener('click', swallow, { capture: true, once: true })
		setTimeout(
			() => window.removeEventListener('click', swallow, { capture: true }),
			0,
		)
	}

	const atStart = progress <= 0
	const atEnd = progress >= 1

	return (
		<div data-motion="rail" className={className}>
			<div className="container flex flex-wrap items-end justify-between gap-6">
				{heading}
				{overflows ? (
					<div className="flex items-center gap-4">
						<span
							aria-hidden="true"
							className="bg-border relative h-px w-16 overflow-hidden sm:w-32"
						>
							<span
								data-slot="rail-progress"
								className="bg-primary ease-out-expo duration-medium absolute inset-0 origin-left transition-transform"
								style={{ transform: `scaleX(${Math.max(progress, 0.12)})` }}
							/>
						</span>
						<RailButton
							label={previousLabel}
							icon="arrow-left"
							disabled={atStart}
							onClick={() => step(-1)}
						/>
						<RailButton
							label={nextLabel}
							icon="arrow-right"
							disabled={atEnd}
							onClick={() => step(1)}
						/>
					</div>
				) : null}
			</div>

			<div
				ref={scrollerRef}
				data-slot="rail-scroller"
				onScroll={measure}
				onPointerDown={startDrag}
				onPointerMove={moveDrag}
				onPointerUp={endDrag}
				onPointerLeave={endDrag}
				onPointerCancel={endDrag}
				// A dragged link or image would start a native drag and stop the
				// pointer events.
				onDragStart={(event) => event.preventDefault()}
				className="scrollbar-none mt-8 flex snap-x snap-mandatory gap-6 overflow-x-auto pt-2 pb-6 select-none"
				style={{
					paddingInline: containerInset,
					scrollPaddingInline: containerInset,
				}}
			>
				{items.map((item) => (
					<div
						key={item.key}
						data-slot="rail-item"
						className="w-[min(80vw,22rem)] shrink-0 snap-start"
					>
						{item.node}
					</div>
				))}
			</div>
		</div>
	)
}

function RailButton({
	label,
	icon,
	disabled,
	onClick,
}: {
	label: string
	icon: 'arrow-left' | 'arrow-right'
	disabled: boolean
	onClick: () => void
}) {
	// `aria-disabled` rather than `disabled`, so focus stays on a button that
	// reaches the end of the rail.
	return (
		<Button
			variant="outline"
			size="icon"
			aria-label={label}
			aria-disabled={disabled || undefined}
			onClick={disabled ? undefined : onClick}
			className="aria-disabled:hover:bg-control-glass aria-disabled:hover:text-foreground size-11 rounded-full aria-disabled:cursor-default aria-disabled:opacity-40"
		>
			<Icon name={icon} size="sm" />
		</Button>
	)
}
